import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';

import { CustomTabBarIndicator } from '@/components/CustomTabBarIndicator';
import { LeftSideTabBar, LeftSideTabBarProps } from '@/components/LeftSideTabBar';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useGQLRequestPolicy } from '@/feature/guest-user/hooks/use-gql-request-policy';
import { lobbyId, promosId, useLobbySectionSelection } from '@/feature/lobby/hooks/use-lobby-section-selection';
import { useGetOrderedLeaguesByEvents } from '@/hooks/use-fantasy-league-configs';
import { useResumeEffect } from '@/hooks/use-resume';
import { Defined } from '@/types/utils';

import { PickemLeagueTile } from './PickemLeagueTile';
import { PickemLobbyLaunchpadTile } from './PickemLobbyLaunchpadTile';
import { PickemLobbyPromosTile } from './PickemLobbyPromosTile';
import { LobbyLeagueSkeleton } from './Skeletons/LobbyLeagueSkeleton';

export const skeletonArray = Array.from({ length: 16 }, (_, index) => ({
    key: `loading-${index}`,
    id: `${index}`,
    title: `${index}`,
}));

export const LEAGUES_LIST_PADDING = 16;

export const LobbyLeagues = memo(() => {
    const requestPolicy = useGQLRequestPolicy('cache-and-network');
    const { fetching, isSorting, refetch: reload, sortedLeagues } = useGetOrderedLeaguesByEvents(false, requestPolicy);

    const selectedTile = useLobbySectionSelection(state => state.selectedTile);

    const [tabIndex, setTabIndex] = useState(0);

    useResumeEffect(reload);

    const leagues = useMemo(() => {
        return fetching || isSorting
            ? skeletonArray
            : [
                  { key: 'lobby', id: 'lobby', title: 'Lobby' },
                  // always render promos tab with leagues
                  { key: 'promos', id: 'promos', title: 'Promos' },
                  ...sortedLeagues.map(item => ({
                      key: item.league.league,
                      id: item.league.league,
                      title: item.league.label,
                  })),
              ].filter(Boolean);
    }, [fetching, isSorting, sortedLeagues]);

    useEffect(() => {
        // we need to change the tab index if the selectedTile changes from the outside
        if (selectedTile) {
            const index = leagues.findIndex(item => item.id === selectedTile);
            // we need to check if the index is different from the current tabIndex to avoid redundant updates
            if (index !== -1 && index !== tabIndex) {
                // Wait for layout pass to complete before updating tab position (fixes deep link indicator bug)
                requestAnimationFrame(() => {
                    setTabIndex(index);
                });
            }
        }
    }, [leagues, selectedTile, tabIndex]);

    const setSelectedTab = useCallback(
        (id: string) => {
            setTabIndex(leagues.findIndex(item => item.id === id));
        },
        [leagues]
    );

    const renderTabBarItem = useCallback<Defined<LeftSideTabBarProps['renderTabBarItem']>>(
        props => {
            const { onLayout, onPress, route, key, navigationState } = props;
            if (fetching || isSorting) {
                return <LobbyLeagueSkeleton />;
            }
            if (route.key === lobbyId) {
                return <PickemLobbyLaunchpadTile onLayout={onLayout} updateTab={onPress} />;
            }

            if (route.key === promosId) {
                return <PickemLobbyPromosTile onLayout={onLayout} updateTab={onPress} />;
            }

            const item = sortedLeagues.find(it => it.league.league === route.key);

            if (!item) {
                return <Box />;
            }

            const isLastItem = key === navigationState.routes[navigationState.routes.length - 1].key;

            return (
                <PickemLeagueTile
                    isLastItem={isLastItem}
                    updateTab={onPress}
                    onLayout={onLayout}
                    {...item.league}
                    eventsCount={item.eventsCount}
                    isLive={item.hasLiveEvents}
                    testID={`lobby-league-tile-${item.league.label}`}
                />
            );
        },
        [fetching, isSorting, sortedLeagues]
    );

    return (
        // GestureDetector added to:
        // - fix Android indicator lag (PIC-6662)
        // - Preserve horizontal tab scrolling PIC-6249
        <GestureDetector gesture={Gesture.Native().shouldActivateOnStart(true)}>
            <LeftSideTabBar
                containerStyle={[styles.tabBar, styles.border]}
                indicatorStyle={styles.indicator}
                tabBarStyle={styles.tabBar}
                tabs={leagues}
                initialIndex={tabIndex}
                renderIndicator={LeaguesIndicator}
                setSelectedTab={setSelectedTab}
                // the gap is managed by each item + custom styling
                gap={0}
                location="lobby-leagues"
                renderTabBarItem={renderTabBarItem}
                testID={'lobby-leagues-tab-bar'}
            />
        </GestureDetector>
    );
});

/**
 * Custom indicator for the Leagues List.
 *
 * In the design, the tabs have a gap of 16, and the list has a horizontal padding of 16.
 * We couldn't find a way to achieve this solely with TabView props while maintaining correct spacing
 * and alignment of the indicator
 *
 * To work around this, a paddingLeft is added to each item,
 * except for the last one which gets both paddingLeft and paddingRight
 * This results in an incorrect tab indicator width.
 *
 * To adjust, we render the default indicator with a custom width
 *
 */
export const LeaguesIndicator: LeftSideTabBarProps['renderIndicator'] = indicatorProps => {
    const { index, routes } = indicatorProps.navigationState;

    const width = indicatorProps.getTabWidth(index);
    const isLastItem = index === routes.length - 1;

    const paddingHorizontal = isLastItem ? LEAGUES_LIST_PADDING * 2 : LEAGUES_LIST_PADDING;

    const correctedWidth = width > 0 ? width - paddingHorizontal : 0;

    return <CustomTabBarIndicator {...indicatorProps} width={correctedWidth} />;
};

export const LEAGUES_LIST_HEIGHT = 100;

export const styles = StyleSheet.create({
    indicator: {
        marginLeft: LEAGUES_LIST_PADDING / 2,
    },
    tabBar: {
        height: LEAGUES_LIST_HEIGHT,
        marginHorizontal: 0,
    },
    border: {
        borderBottomWidth: 1,
        borderBottomColor: ds.colorValue.border.strong,
        borderStyle: 'solid',
    },
});
